import { useState } from "react";

import type { Blatt as BlattName, VertragsSchalter } from "../hooks/useVertragstext";
import {
  BLAETTER, BLATT_LABEL, STATUS_LABEL, VERTRAGS_VORSCHAU_START, ZAHLWEISE_WAHL,
  fehlertext, useFassungen, useVertragsVorschau,
} from "../hooks/useVertragstext";
import VertragAnsicht from "./VertragAnsicht";

/**
 * Der echte Vertrag, so wie der Kunde ihn sehen wird.
 *
 * Sie ersetzt „Vertrag als PDF". Ein PDF muss im Büro niemand erzeugen — der
 * Kunde bekommt seines über das Onboarding. Gebraucht wird die Antwort auf
 * „wie sieht es bei ihm aus", und zwar unter den Entscheidungen, die er
 * treffen wird, nicht nur unter der einen, die gerade gespeichert ist.
 *
 * Deshalb die Schalter: Zahlweise und Optionen lassen sich durchspielen, ohne
 * dass etwas geschrieben wird. Gerendert wird auf dem Server mit demselben
 * Renderer, der das PDF baut.
 *
 * **Nicht zu verwechseln mit der Ausfertigung.** Ab der Zustimmung liegt das
 * Dokument unveränderlich in der Datenbank; dafür gibt es weiterhin einen
 * eigenen Knopf, der genau diese Bytes liefert. Diese Vorschau zeigt immer den
 * laufenden Stand — sie ist ein Blick nach vorn, kein Nachweis.
 */
export default function VertragDokumentVorschau({ vertragId }: { vertragId: string }) {
  const [schalter, setSchalter] = useState<VertragsSchalter>(VERTRAGS_VORSCHAU_START);
  const [blatt, setBlatt] = useState<BlattName>("urkunde");
  const { data, isLoading, error } = useVertragsVorschau(vertragId, schalter);
  const { data: fassungsliste } = useFassungen();
  const fassungen = fassungsliste?.fassungen;
  const setzen = (teil: Partial<VertragsSchalter>) => setSchalter({ ...schalter, ...teil });

  const html = !data ? "" :
    blatt === "agb" ? data.agb_html :
    blatt === "anlage" ? data.anlage_html :
    blatt === "mandat" ? data.mandat_html :
    blatt === "bestand" ? data.bestand_html :
    data.paragrafen_html + data.anhaenge_html;

  // Welche Optionen gerade an sind. Solange niemand geschaltet hat, gilt der
  // gespeicherte Stand — den kennt erst die Antwort des Servers.
  const anGeschaltet = (id: string) =>
    schalter.beauftragt === null
      ? !!data?.optionale.find(o => o.id === id)?.beauftragt
      : schalter.beauftragt.includes(id);

  const umschalten = (id: string) => {
    const jetzt = data?.optionale.filter(o => anGeschaltet(o.id)).map(o => o.id) ?? [];
    setzen({
      beauftragt: jetzt.includes(id) ? jetzt.filter(x => x !== id) : [...jetzt, id],
    });
  };

  return (
    <div className="vt-vorschau">
      <div className="vt-schalter">
        <div className="vt-schalter-zeile">
          <select className="pf-input is-tight"
                  value={schalter.zahlweise ?? ""}
                  onChange={e => setzen({
                    zahlweise: e.target.value,
                    // „noch offen" ist keine Zahlweise, sondern ihr Fehlen —
                    // der Renderer braucht dafür den eigenen Schalter.
                    zahlweise_offen: e.target.value === "",
                  })}>
            {ZAHLWEISE_WAHL.map(z => (
              <option key={z.wert} value={z.wert}>{z.text}</option>
            ))}
          </select>
          <select className="pf-input is-tight" value={blatt}
                  onChange={e => setBlatt(e.target.value as BlattName)}>
            {BLAETTER.map(b => (
              <option key={b} value={b}>{BLATT_LABEL[b]}</option>
            ))}
          </select>
          {/* Diesen Vertrag testweise unter einer anderen Textfassung ansehen.
              Ohne das ließe sich eine neue Fassung nur am Beispielkunden
              beurteilen — und der hat weder sechs Platanen noch einen Hang. */}
          <select className="pf-input is-tight" value={schalter.werk_id ?? ""}
                  onChange={e => setzen({ werk_id: e.target.value || null })}>
            <option value="">Fassung des Vertrages</option>
            {(fassungen ?? []).map(f => (
              <option key={f.id} value={f.id}>
                V{f.version} · {STATUS_LABEL[f.status] ?? f.status}
              </option>
            ))}
          </select>
        </div>

        {data && data.optionale.length > 0 && (
          <div className="vt-schalter-flags">
            {data.optionale.map(o => (
              <label key={o.id}
                     className={"vt-flag" + (anGeschaltet(o.id) ? " is-on" : "")}>
                <input type="checkbox" checked={anGeschaltet(o.id)}
                       onChange={() => umschalten(o.id)} />
                <span>{o.titel}</span>
              </label>
            ))}
          </div>
        )}

        <div className="vt-hint">
          Nichts hiervon wird gespeichert — die Schalter zeigen nur, wie der
          Vertrag unter dieser Wahl aussähe.
        </div>
      </div>

      {error ? (
        <div className="vt-fehler">
          <strong>Der Vertrag rendert in dieser Variante nicht.</strong>
          <div>{fehlertext(error)}</div>
          <div className="vt-hint">
            Der Fehler steckt im Vertragstext oder in den Daten dieses Vertrages,
            nicht im Server. In dieser Variante bekäme der Kunde nichts zu sehen.
          </div>
        </div>
      ) : isLoading && !data ? (
        <div className="pf-empty">Rendere…</div>
      ) : data ? (
        <>
          <Seitenzahl blatt={blatt} seiten={data.seiten} />
          <VertragAnsicht css={data.css} html={html} bilder={data.seiten_bilder?.[blatt]} />
        </>
      ) : null}
    </div>
  );
}

/**
 * Wie viele Seiten dieses Blatt hat — gemessen, nicht geschätzt.
 *
 * Zwei Seiten sind das Ziel der Urkunde. Eine Zahl, die danebensteht, hält die
 * Zusage wach; eine Erinnerung tut das nach der dritten Textänderung nicht
 * mehr. **Kein Riegel:** ein Garten mit dreißig Positionen braucht mehr, und
 * dann ist das richtig.
 */
function Seitenzahl({ blatt, seiten }: {
  blatt: BlattName;
  seiten: Record<string, number | undefined>;
}) {
  const n = seiten?.[blatt];
  if (!n) return null;
  const knapp = blatt === "urkunde" && n > 2;
  return (
    <div className={"vt-seitenzahl" + (knapp ? " is-warn" : "")}>
      <strong>{BLATT_LABEL[blatt]}: {n} {n === 1 ? "Seite" : "Seiten"}</strong>
      {blatt === "urkunde" && (knapp
        ? " — angestrebt sind zwei. Leistungsverzeichnis, Jahresplan und "
          + "Bestandsaufnahme gehören in die Anlagen."
        : " — das Ziel ist gehalten.")}
      {seiten.widerruf ? ` · Widerrufsbelehrung: ${seiten.widerruf}` : ""}
    </div>
  );
}
